import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Star, Trophy, DollarSign, Truck, CheckCircle,
  AlertTriangle, XCircle, Clock, ChevronRight, Shield, MessageSquare, Pencil
} from 'lucide-react';
import { StarRating } from '../components/TrustBadge';
import { ShippingInfoDialog } from '../components/ShippingInfoDialog';
import OffersSection from '../components/OffersSection';
import EditListingDialog from '../components/EditListingDialog';

const tg = () => window.Telegram?.WebApp;
const haptic = (type = 'impact', style = 'light') => {
  try {
    if (type === 'impact') tg()?.HapticFeedback?.impactOccurred(style);
    else if (type === 'notification') tg()?.HapticFeedback?.notificationOccurred(style);
  } catch (e) {}
};

function MyGames() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [myGames, setMyGames] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShipDialog, setShowShipDialog] = useState(false);
  const [selectedEscrow, setSelectedEscrow] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [ratingEscrow, setRatingEscrow] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingReview, setRatingReview] = useState('');
  const [showEvidenceDialog, setShowEvidenceDialog] = useState(false);
  const [evidenceEscrow, setEvidenceEscrow] = useState(null);
  const [evidenceText, setEvidenceText] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [showShippingDialog, setShowShippingDialog] = useState(false);
  const [shippingEscrow, setShippingEscrow] = useState(null);
  const [autoPromptedFor, setAutoPromptedFor] = useState(new Set());
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [decisionEscrow, setDecisionEscrow] = useState(null); // Sell/Keep dialog target
  const [decisionLoading, setDecisionLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchData();

    const telegram = tg();
    if (telegram) {
      telegram.BackButton.show();
      telegram.BackButton.onClick(() => navigate('/'));
    }
    return () => {
      if (telegram) {
        telegram.BackButton.hide();
        telegram.BackButton.offClick(() => navigate('/'));
      }
    };
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gamesRes, escrowRes] = await Promise.all([
        axios.get(`${API}/hosted-games/mine`),
        axios.get(`${API}/escrow/my`)
      ]);
      setMyGames(gamesRes.data.my_games || []);
      const escrowList = escrowRes.data.escrows || [];
      setEscrows(escrowList);
      // Auto-prompt Sell/Keep for any physical prize the winner hasn't decided on yet.
      const needsDecision = escrowList.find(e =>
        e.winner_user_id === user?.user_id
        && e.status === 'pending_shipment'
        && e.prize_type === 'physical'
        && !e.winner_decision
        && !e.shipping_info
        && !autoPromptedFor.has(e.escrow_id)
      );
      if (needsDecision) {
        setDecisionEscrow(needsDecision);
        setAutoPromptedFor(prev => new Set([...prev, needsDecision.escrow_id]));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSellPrize = async () => {
    if (!decisionEscrow) return;
    setDecisionLoading(true);
    try {
      const r = await axios.post(`${API}/escrow/${decisionEscrow.escrow_id}/sell`);
      haptic('notification', 'success');
      toast.success(`Kept ${r.data.winner_kept_xrp.toFixed(2)} XRP — new balance ${r.data.new_balance.toFixed(2)} XRP`);
      setDecisionEscrow(null);
      fetchData();
    } catch (e) {
      haptic('notification', 'error');
      toast.error(e.response?.data?.detail || 'Sell failed');
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleKeepPrize = async () => {
    if (!decisionEscrow) return;
    setDecisionLoading(true);
    try {
      await axios.post(`${API}/escrow/${decisionEscrow.escrow_id}/keep`);
      haptic('impact', 'light');
      // Immediately open the shipping form on the same escrow.
      const escrow = decisionEscrow;
      setDecisionEscrow(null);
      setShippingEscrow(escrow);
      setShowShippingDialog(true);
    } catch (e) {
      haptic('notification', 'error');
      toast.error(e.response?.data?.detail || 'Failed to record decision');
    } finally {
      setDecisionLoading(false);
    }
  };

  const handleCancelGame = async (gameId) => {
    try {
      await axios.delete(`${API}/hosted-games/${gameId}`);
      haptic('notification', 'success');
      toast.success('Game cancelled');
      fetchData();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Cannot cancel');
    }
  };

  const handleMarkShipped = async () => {
    if (!selectedEscrow) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/escrow/${selectedEscrow.escrow_id}/action`, {
        action: 'mark_shipped',
        tracking_number: trackingNumber,
        note: 'Shipped with signature required'
      });
      haptic('notification', 'success');
      toast.success('Marked as shipped!');
      setShowShipDialog(false);
      setSelectedEscrow(null);
      setTrackingNumber('');
      fetchData();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReceived = async (escrowId) => {
    try {
      await axios.post(`${API}/escrow/${escrowId}/action`, {
        action: 'confirm_received',
        note: 'Item received in good condition'
      });
      haptic('notification', 'success');
      toast.success('Receipt confirmed! Escrow released.');
      fetchData();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleSubmitRating = async () => {
    if (!ratingEscrow || ratingValue === 0) return;
    setActionLoading(true);
    try {
      const isHost = ratingEscrow.host_user_id === user?.user_id;
      await axios.post(`${API}/ratings/submit`, {
        escrow_id: ratingEscrow.escrow_id,
        rating: ratingValue,
        review: ratingReview,
        type: isHost ? 'buyer' : 'seller'
      });
      haptic('notification', 'success');
      toast.success('Rating submitted!');
      setShowRatingDialog(false);
      setRatingEscrow(null);
      setRatingValue(0);
      setRatingReview('');
      fetchData();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitEvidence = async () => {
    if (!evidenceEscrow) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/escrow/${evidenceEscrow.escrow_id}/evidence`, {
        description: evidenceText,
        image_url: evidenceUrl,
        evidence_type: evidenceUrl ? 'image' : 'text'
      });
      haptic('notification', 'success');
      toast.success('Evidence submitted!');
      setShowEvidenceDialog(false);
      setEvidenceEscrow(null);
      setEvidenceText('');
      setEvidenceUrl('');
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25', label: 'Active' },
      completed: { color: 'bg-blue-500/15 text-blue-300 border-blue-500/25', label: 'Completed' },
      cancelled: { color: 'bg-slate-500/15 text-slate-400 border-slate-500/25', label: 'Cancelled' },
      refunded: { color: 'bg-amber-500/15 text-amber-300 border-amber-500/25', label: 'Refunded' },
    };
    const b = badges[status] || badges.active;
    return <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${b.color}`}>{b.label}</span>;
  };

  const getEscrowIcon = (status) => {
    switch (status) {
      case 'pending_shipment': return <Clock className="w-4 h-4 text-amber-400" />;
      case 'shipped': return <Truck className="w-4 h-4 text-blue-400" />;
      case 'completed': case 'auto_released': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'disputed': return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'refunded': return <XCircle className="w-4 h-4 text-slate-400" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto"></div>
      </div>
    );
  }

  const totalEarned = myGames.reduce((sum, g) => sum + (g.total_earned || 0), 0);
  const totalHeld = myGames.reduce((sum, g) => sum + (g.held_in_escrow || 0), 0);
  const activeGames = myGames.filter(g => g.status === 'active').length;

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3 flex items-center gap-3">
          <button data-testid="back-button" onClick={() => navigate('/')} className="text-slate-400 hover:text-white p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-base font-bold text-white leading-tight">My Games</h1>
            <p className="text-[11px] text-slate-500">Host dashboard</p>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="px-4 pt-4 grid grid-cols-3 gap-2">
        <div data-testid="stat-active" className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-white">{activeGames}</p>
          <p className="text-[10px] text-slate-500">Active</p>
        </div>
        <div data-testid="stat-earned" className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-emerald-400">{totalEarned.toFixed(1)}</p>
          <p className="text-[10px] text-slate-500">Earned (XRP)</p>
        </div>
        <div data-testid="stat-held" className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-amber-400">{totalHeld.toFixed(1)}</p>
          <p className="text-[10px] text-slate-500">In Escrow</p>
        </div>
      </div>

      {/* My Listed Games */}
      <section className="px-4 pt-5">
        <h3 data-testid="my-rooms-heading" className="text-sm font-bold text-white mb-3">My Rooms ({myGames.length})</h3>
        {myGames.length === 0 ? (
          <div className="text-center py-8 bg-slate-800/30 border border-dashed border-slate-700/50 rounded-2xl">
            <Package className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No games hosted yet</p>
            <Button data-testid="host-from-mygames" onClick={() => navigate('/')} size="sm" variant="outline" className="mt-3 border-purple-500/40 text-purple-300 text-xs h-8">
              Go Host a Game
            </Button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {myGames.map((game) => (
              <Card key={game.game_id} data-testid={`my-game-${game.game_id.slice(0,8)}`} className="bg-slate-800/50 border-slate-700/50">
                <div className="p-3.5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {game.prize_type === 'nft' ? <Star className="w-4 h-4 text-purple-400" /> : <Package className="w-4 h-4 text-purple-400" />}
                      <div>
                        <p className="text-sm font-bold text-white">{game.room_name}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{game.prize_description}</p>
                      </div>
                    </div>
                    {getStatusBadge(game.status)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 bg-slate-900/40 rounded-lg p-2">
                    <div className="text-center">
                      <p className="text-xs font-bold text-indigo-300">{game.total_slots_sold}/10</p>
                      <p className="text-[9px] text-slate-500">Slots Sold</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-bold text-emerald-300">{game.total_earned?.toFixed(2)}</p>
                      <p className="text-[9px] text-slate-500">Earned XRP</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-bold text-amber-300">{game.potential_earnings?.toFixed(2)}</p>
                      <p className="text-[9px] text-slate-500">Max Earn</p>
                    </div>
                  </div>
                  {game.status === 'active' && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        data-testid={`edit-game-${game.game_id.slice(0,8)}`}
                        onClick={() => { setEditingListing({ kind: 'hosted', data: game }); setShowEditDialog(true); }}
                        size="sm"
                        variant="ghost"
                        className="flex-1 text-amber-300 hover:bg-amber-500/10 text-[11px] h-7"
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      {game.total_slots_sold === 0 && (
                        <Button
                          data-testid={`cancel-game-${game.game_id.slice(0,8)}`}
                          onClick={() => handleCancelGame(game.game_id)}
                          size="sm"
                          variant="ghost"
                          className="flex-1 text-red-400 hover:bg-red-500/10 text-[11px] h-7"
                        >
                          <XCircle className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Offers (inbox + sent) */}
      <OffersSection />

      {/* Escrows */}
      {escrows.length > 0 && (
        <section className="px-4 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-orange-400" />
            <h3 data-testid="escrows-heading" className="text-sm font-bold text-white">Escrows ({escrows.length})</h3>
          </div>
          <div className="space-y-2.5">
            {escrows.map((escrow) => {
              const isHost = escrow.host_user_id === user?.user_id;
              const isWinner = escrow.winner_user_id === user?.user_id;
              return (
                <Card key={escrow.escrow_id} data-testid={`escrow-${escrow.escrow_id.slice(0,8)}`} className="bg-slate-800/50 border-slate-700/50">
                  <div className="p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getEscrowIcon(escrow.status)}
                        <div>
                          <p className="text-xs font-semibold text-white">{escrow.prize_description}</p>
                          <p className="text-[10px] text-slate-500">
                            {isHost ? 'You are the host' : 'You won this prize'}
                            {escrow.escrow_amount > 0 && ` — ${escrow.escrow_amount.toFixed(2)} XRP`}
                          </p>
                        </div>
                      </div>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold capitalize ${
                        escrow.status === 'completed' || escrow.status === 'auto_released' ? 'bg-emerald-500/15 text-emerald-300' :
                        escrow.status === 'disputed' ? 'bg-red-500/15 text-red-300' :
                        escrow.status === 'shipped' ? 'bg-blue-500/15 text-blue-300' :
                        'bg-amber-500/15 text-amber-300'
                      }`}>{escrow.status.replace('_', ' ')}</span>
                    </div>
                    {escrow.tracking_number && (
                      <div className="bg-slate-900/40 rounded-lg px-2.5 py-1.5 mb-2">
                        <p className="text-[10px] text-slate-500">Tracking</p>
                        <p className="text-xs text-blue-300 font-mono">{escrow.tracking_number}</p>
                      </div>
                    )}
                    {/* Order # + Shipping address (visible to both parties once submitted) */}
                    {escrow.order_number && (
                      <div data-testid={`order-info-${escrow.escrow_id.slice(0,8)}`} className="bg-slate-900/40 rounded-lg px-2.5 py-2 mb-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] text-slate-500 uppercase tracking-wide">Order</span>
                          <span className="text-[11px] text-white font-mono font-bold">{escrow.order_number}</span>
                        </div>
                        {escrow.shipping_info && (
                          <>
                            <div className="border-t border-slate-700/40 pt-1">
                              <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Ship to</p>
                              <p className="text-[10px] text-slate-200 leading-snug">
                                {escrow.shipping_info.full_name}<br/>
                                {escrow.shipping_info.address_line1}{escrow.shipping_info.address_line2 ? `, ${escrow.shipping_info.address_line2}` : ''}<br/>
                                {[escrow.shipping_info.city, escrow.shipping_info.state, escrow.shipping_info.postal_code].filter(Boolean).join(', ')}<br/>
                                {escrow.shipping_info.country}
                                {escrow.shipping_info.phone && <><br/>Tel: {escrow.shipping_info.phone}</>}
                              </p>
                              {escrow.shipping_info.notes && (
                                <p className="text-[10px] text-amber-300/80 mt-1 italic">"{escrow.shipping_info.notes}"</p>
                              )}
                            </div>
                          </>
                        )}
                        {escrow.ship_deadline && escrow.status === 'pending_shipment' && (
                          <p className="text-[9px] text-amber-400 pt-0.5">
                            Ship by {new Date(escrow.ship_deadline).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Winner: choose Sell or Keep (physical only, no info yet, no decision yet). Shop purchases skip sell-back. */}
                    {isWinner && escrow.prize_type === 'physical' && escrow.status === 'pending_shipment' && !escrow.shipping_info && escrow.source !== 'shop' && (
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <Button
                          data-testid={`sell-back-btn-${escrow.escrow_id.slice(0,8)}`}
                          onClick={() => setDecisionEscrow(escrow)}
                          size="sm"
                          variant="outline"
                          className="text-xs h-8 border-orange-500/50 bg-orange-500/5 hover:bg-orange-500/15 text-orange-300"
                          title={`Sell prize for 32.1% (${(Number(escrow.escrow_amount || 0) * 0.321).toFixed(2)} XRP)`}
                        >
                          💰 Sell 32.1%
                        </Button>
                        <Button
                          data-testid={`provide-shipping-${escrow.escrow_id.slice(0,8)}`}
                          onClick={() => { setShippingEscrow(escrow); setShowShippingDialog(true); }}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8"
                        >
                          <Truck className="w-3 h-3 mr-1" /> Keep + Ship
                        </Button>
                      </div>
                    )}
                    {/* Shop purchase: straight to shipping address (no sell-back) */}
                    {isWinner && escrow.source === 'shop' && escrow.prize_type === 'physical' && escrow.status === 'pending_shipment' && !escrow.shipping_info && (
                      <Button
                        data-testid={`shop-provide-shipping-${escrow.escrow_id.slice(0,8)}`}
                        onClick={() => { setShippingEscrow(escrow); setShowShippingDialog(true); }}
                        size="sm"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-xs h-8 mt-1"
                      >
                        <Truck className="w-3 h-3 mr-1" /> Provide Shipping Address
                      </Button>
                    )}
                    {/* Host actions (only when shipping_info present OR non-physical) */}
                    {isHost && escrow.status === 'pending_shipment' && (escrow.prize_type !== 'physical' || escrow.shipping_info) && (
                      <Button
                        data-testid={`ship-btn-${escrow.escrow_id.slice(0,8)}`}
                        onClick={() => { setSelectedEscrow(escrow); setShowShipDialog(true); }}
                        size="sm"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-xs h-8 mt-1"
                      >
                        <Truck className="w-3 h-3 mr-1" /> Mark as Shipped
                      </Button>
                    )}
                    {/* Host waiting on winner */}
                    {isHost && escrow.status === 'pending_shipment' && escrow.prize_type === 'physical' && !escrow.shipping_info && (
                      <p data-testid={`waiting-shipping-${escrow.escrow_id.slice(0,8)}`} className="text-[10px] text-amber-400 mt-1 italic">
                        Waiting for winner to submit shipping address...
                      </p>
                    )}
                    {/* Winner actions */}
                    {isWinner && escrow.status === 'shipped' && (
                      <Button
                        data-testid={`confirm-btn-${escrow.escrow_id.slice(0,8)}`}
                        onClick={() => handleConfirmReceived(escrow.escrow_id)}
                        size="sm"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-xs h-8 mt-1"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" /> Confirm Received
                      </Button>
                    )}
                    {/* Rate button — completed escrows, not yet rated */}
                    {(escrow.status === 'completed' || escrow.status === 'auto_released') && !escrow.user_has_rated && (
                      <Button
                        data-testid={`rate-btn-${escrow.escrow_id.slice(0,8)}`}
                        onClick={() => { setRatingEscrow(escrow); setShowRatingDialog(true); }}
                        size="sm"
                        variant="outline"
                        className="w-full border-yellow-500/30 text-yellow-300 text-xs h-8 mt-1"
                      >
                        <Star className="w-3 h-3 mr-1" /> Rate Transaction
                      </Button>
                    )}
                    {/* Evidence button — disputed escrows */}
                    {escrow.status === 'disputed' && (
                      <Button
                        data-testid={`evidence-btn-${escrow.escrow_id.slice(0,8)}`}
                        onClick={() => { setEvidenceEscrow(escrow); setShowEvidenceDialog(true); }}
                        size="sm"
                        variant="outline"
                        className="w-full border-red-500/30 text-red-300 text-xs h-8 mt-1"
                      >
                        <MessageSquare className="w-3 h-3 mr-1" /> Submit Evidence
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Sell-or-Keep decision dialog — first thing a physical-prize winner sees */}
      <Dialog open={!!decisionEscrow} onOpenChange={(open) => { if (!open && !decisionLoading) setDecisionEscrow(null); }}>
        <DialogContent data-testid="sell-or-keep-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">🎉 You won a physical prize!</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Choose how you want to receive your reward
            </DialogDescription>
          </DialogHeader>
          {decisionEscrow && (() => {
            const pool = Number(decisionEscrow.escrow_amount || 0);
            const sellValue = +(pool * 0.321).toFixed(2);
            const fairMarket = Number(decisionEscrow.prize_value_xrp || 0);
            const upside = fairMarket > 0 ? +(fairMarket - sellValue).toFixed(2) : null;
            return (
              <div className="space-y-3 pt-2">
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Prize</span>
                    <span className="text-white font-medium text-right truncate ml-2 max-w-[60%]">
                      {decisionEscrow.prize_description || 'Physical prize'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Pool won</span>
                    <span className="text-emerald-300 font-semibold">{pool.toFixed(2)} XRP</span>
                  </div>
                  {fairMarket > 0 && (
                    <div className="flex justify-between" data-testid="prize-fair-value-row">
                      <span className="text-slate-400">Prize value</span>
                      <span className="text-white font-semibold">{fairMarket.toFixed(2)} XRP</span>
                    </div>
                  )}
                </div>

                {/* Sell option */}
                <button
                  data-testid="sell-prize-button"
                  onClick={handleSellPrize}
                  disabled={decisionLoading}
                  className="w-full text-left rounded-xl border border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-amber-500/5 hover:from-orange-500/20 hover:to-amber-500/10 p-3 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-orange-300">💰 Sell for cash</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Keep {sellValue.toFixed(2)} XRP (32.1% of pool)</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Host retains the physical prize</p>
                    </div>
                    <span className="text-lg font-black text-orange-400">{sellValue.toFixed(2)}</span>
                  </div>
                </button>

                {/* Keep option */}
                <button
                  data-testid="keep-prize-button"
                  onClick={handleKeepPrize}
                  disabled={decisionLoading}
                  className="w-full text-left rounded-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 hover:from-emerald-500/20 hover:to-teal-500/10 p-3 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-emerald-300">📦 Keep the prize</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Free shipping to your door
                        {fairMarket > 0 && <> · <span className="text-emerald-400">worth {fairMarket.toFixed(2)} XRP</span></>}
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Prize is yours to keep</p>
                    </div>
                    <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">Free</span>
                  </div>
                </button>

                {/* Nudge showing upside of keeping */}
                {fairMarket > 0 && upside > 0.5 && (
                  <p data-testid="keep-upside-nudge" className="text-[10px] text-emerald-300/90 text-center leading-snug">
                    Keeping the prize is <span className="font-bold">{upside.toFixed(2)} XRP more valuable</span> than selling for cash.
                  </p>
                )}

                <p className="text-[9px] text-slate-500 text-center leading-snug">
                  Once you enter shipping details, selling is no longer available.
                </p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Ship Dialog */}
      <Dialog open={showShipDialog} onOpenChange={setShowShipDialog}>
        <DialogContent data-testid="ship-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Mark as Shipped</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Ship with signature-required delivery. Package must be well protected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5">
              <p className="text-[11px] text-amber-300 font-medium">Reminder: Signature required on delivery</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Winner has 14 days to confirm. Auto-releases after.</p>
              <p className="text-[10px] text-red-300/90 mt-1">Signature-required shipping is mandatory — if a physical item ships without it, the sale is final and non-delivery disputes won't be covered.</p>
            </div>
            <Input
              data-testid="tracking-input"
              placeholder="Tracking number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <Button
              data-testid="confirm-ship-btn"
              onClick={handleMarkShipped}
              disabled={actionLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 h-10"
            >
              {actionLoading ? 'Processing...' : 'Confirm Shipped'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent data-testid="rating-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Rate Transaction</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {ratingEscrow?.prize_description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-center">
              <p className="text-xs text-slate-400 mb-2">
                Rate the {ratingEscrow?.host_user_id === user?.user_id ? 'buyer' : 'seller'}
              </p>
              <StarRating rating={ratingValue} onRate={setRatingValue} />
            </div>
            <Input
              data-testid="rating-review"
              placeholder="Optional review..."
              value={ratingReview}
              onChange={(e) => setRatingReview(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <Button
              data-testid="submit-rating-btn"
              onClick={handleSubmitRating}
              disabled={actionLoading || ratingValue === 0}
              className="w-full bg-yellow-600 hover:bg-yellow-700 h-10"
            >
              {actionLoading ? 'Submitting...' : 'Submit Rating'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Evidence Dialog */}
      <Dialog open={showEvidenceDialog} onOpenChange={setShowEvidenceDialog}>
        <DialogContent data-testid="evidence-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Submit Evidence</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Provide evidence to support your case in this dispute.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              data-testid="evidence-text"
              placeholder="Describe what happened..."
              value={evidenceText}
              onChange={(e) => setEvidenceText(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <Input
              data-testid="evidence-url"
              placeholder="Photo URL (optional)"
              value={evidenceUrl}
              onChange={(e) => setEvidenceUrl(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <p className="text-[9px] text-slate-500">Upload your photo to any image host and paste the URL above.</p>
            <Button
              data-testid="submit-evidence-btn"
              onClick={handleSubmitEvidence}
              disabled={actionLoading || (!evidenceText && !evidenceUrl)}
              className="w-full bg-red-600 hover:bg-red-700 h-10"
            >
              {actionLoading ? 'Submitting...' : 'Submit Evidence'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shipping Info Dialog (winner provides address) */}
      <ShippingInfoDialog
        escrow={shippingEscrow}
        open={showShippingDialog}
        onOpenChange={(v) => { setShowShippingDialog(v); if (!v) setShippingEscrow(null); }}
        onSubmitted={() => { fetchData(); }}
        prefillEmail={user?.email}
      />

      {/* Edit listing dialog (hosts only) */}
      <EditListingDialog
        open={showEditDialog}
        onOpenChange={(v) => { setShowEditDialog(v); if (!v) setEditingListing(null); }}
        kind={editingListing?.kind || 'hosted'}
        listing={editingListing?.data || {}}
        onUpdated={() => fetchData()}
      />
    </div>
  );
}

export default MyGames;
